import { id } from '@monorepo/tools/src/lib/types/primitives';
import { Status } from '../enums/status';
import { makeAutoObservable } from 'mobx';

function normalizeBadIspWords(value: unknown): string | undefined {
	if (value === undefined || value === null) {
		return undefined;
	}
	if (Array.isArray(value)) {
		return value.map(String).join(', ');
	}
	return typeof value === 'string' ? value : String(value);
}

interface IBidderAdvertiserTable {
	id: number;
	name: string;
	status: string;
	priority: number;
	totalBudget: number;
	totalMoneySpent: number;
	totalMoneyEarn: number;
	dailyMoneySpent: number;
	dailyMoneyEarn: number;
	shouldReceiveL1: boolean;
	useEnhancedBlackList: boolean;
}

export class BidderAdvertiserTableModel {
	id: number;
	name: string;
	status: string;
	priority: number;
	totalBudget: number;
	totalMoneySpent: number;
	totalMoneyEarn: number;
	dailyMoneySpent: number;
	dailyMoneyEarn: number;
	shouldReceiveL1: boolean;
	useEnhancedBlackList: boolean;

	constructor(data: IBidderAdvertiserTable) {
		this.id = data.id;
		this.name = data.name;
		this.status = data.status;
		this.priority = data.priority;
		this.totalMoneySpent = data.totalMoneySpent;
		this.totalMoneyEarn = data.totalMoneyEarn;
		this.dailyMoneySpent = data.dailyMoneySpent;
		this.dailyMoneyEarn = data.dailyMoneyEarn;
		this.totalBudget = data.totalBudget;
		this.shouldReceiveL1 = data.shouldReceiveL1;
		this.useEnhancedBlackList = data.useEnhancedBlackList;
	}
}

export interface IBidderAdvertiser {
	name?: string;
	id?: id;
	status?: Status;
	priority?: number;
	total_money_spent?: number;
	total_money_earn?: number;
	daily_money_spent?: number;
	daily_money_earn?: number;
	total_budget?: number;
	should_receive_l1?: boolean;
	use_enhanced_black_list?: boolean;
	balance?: number;
	newBalance?: number;
	balance_addition?: number;
	bad_isp_words?: string;
}
export class BidderAdvertiserModel implements IBidderAdvertiser {
	private _advertiser_name?: string;
	private _advertiser_id?: id;
	private _advertiser_status?: Status;
	private _priority?: number;
	private _total_money_spent?: number;
	private _total_money_earn?: number;
	private _daily_money_spent?: number;
	private _daily_money_earn?: number;
	private _total_budget?: number;
	private _should_receive_l1?: boolean;
	private _use_enhanced_black_list?: boolean;
	private _balance?: number;
	private _newBalance?: number;
	private _bad_isp_words?: string;

	constructor(data: Partial<IBidderAdvertiser> = {}) {
		this._advertiser_name = data.name;
		this._advertiser_id = data.id;
		this._advertiser_status = data.status;
		this._priority = data.priority;
		this._total_money_spent = data.total_money_spent;
		this._total_money_earn = data.total_money_earn;
		this._daily_money_spent = data.daily_money_spent;
		this._daily_money_earn = data.daily_money_earn;
		this._total_budget = data.total_budget;
		this._should_receive_l1 = data.should_receive_l1;
		this._use_enhanced_black_list = data.use_enhanced_black_list;
		this.balance = data.balance;
		this._bad_isp_words = normalizeBadIspWords(data.bad_isp_words as unknown);

		makeAutoObservable(this);
	}

	get advertiser_name(): string | undefined {
		return this._advertiser_name;
	}
	set advertiser_name(value: string | undefined) {
		this._advertiser_name = value;
	}

	get advertiser_id(): id | undefined {
		return this._advertiser_id;
	}
	set advertiser_id(value: id | undefined) {
		this._advertiser_id = value;
	}

	get advertiser_status(): Status | undefined {
		return this._advertiser_status;
	}
	set advertiser_status(value: Status | undefined) {
		this._advertiser_status = value;
	}

	get priority(): number | undefined {
		return this._priority;
	}
	set priority(value: number | undefined) {
		this._priority = value;
	}

	get total_money_spent(): number | undefined {
		return this._total_money_spent;
	}
	set total_money_spent(value: number | undefined) {
		this._total_money_spent = value;
	}

	get total_money_earn(): number | undefined {
		return this._total_money_earn;
	}
	set total_money_earn(value: number | undefined) {
		this._total_money_earn = value;
	}

	get daily_money_spent(): number | undefined {
		return this._daily_money_spent;
	}
	set daily_money_spent(value: number | undefined) {
		this._daily_money_spent = value;
	}

	get daily_money_earn(): number | undefined {
		return this._daily_money_earn;
	}
	set daily_money_earn(value: number | undefined) {
		this._daily_money_earn = value;
	}

	get total_budget(): number | undefined {
		return this._total_budget;
	}
	set total_budget(value: number | undefined) {
		this._total_budget = value;
	}

	get should_receive_l1(): boolean | undefined {
		return this._should_receive_l1;
	}
	set should_receive_l1(value: boolean | undefined) {
		this._should_receive_l1 = value;
	}

	get use_enhanced_black_list(): boolean | undefined {
		return this._use_enhanced_black_list;
	}
	set use_enhanced_black_list(value: boolean | undefined) {
		this._use_enhanced_black_list = value;
	}

	get balance(): number | undefined {
		return this._balance;
	}
	set balance(value: number | undefined) {
		this._balance = value;
	}

	get newBalance(): number | undefined {
		return this._newBalance;
	}
	set newBalance(value: number | undefined) {
		this._newBalance = value;
	}

	get bad_isp_words(): string | undefined {
		return this._bad_isp_words;
	}

	set bad_isp_words(value: string | undefined) {
		this._bad_isp_words = value;
	}

	toObject(): IBidderAdvertiser {
		return {
			name: this.advertiser_name,
			id: this.advertiser_id,
			status: this.advertiser_status,
			priority: this.priority,
			total_money_spent: this.total_money_spent,
			total_money_earn: this.total_money_earn,
			daily_money_spent: this.daily_money_spent,
			daily_money_earn: this.daily_money_earn,
			total_budget: this.total_budget,
			should_receive_l1: this.should_receive_l1,
			use_enhanced_black_list: this.use_enhanced_black_list,
			balance_addition: this.newBalance ? this.newBalance - this.balance! : 0,
		};
	}
}
